import { IOption } from "components/MainSelect/MainSelectInterfaces";
import {
    currentUserCorporationSelection,
    currentUserDivisionSelection,
    currentUserMillSelection,
    currentUserRegionSelection,
    USERTYPE,
} from "constants/constants";
import { UserContext } from "contexts/UserContext/UserContext";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import { UsersEntity } from "entities/Users/UsersEntity";
import { useContext, useEffect, useMemo, useState } from "react";
import { IRCAExecutiveMillFilter } from "./RCAExecutiveMillFilterInterfaces";
import { useTranslation } from "react-i18next";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { useStyles } from "./RCAExecutiveMillFilter.styles";
import { Box, InputLabel, Typography } from "@mui/material";
import { useRCAMainStyles } from "../../RCA.styles";

const defaultMillsFilterOptionsSelected = currentUserMillSelection;
const RCAExecutiveMillFilter = ({
    onSetExecutiveMillFilters,
    onComponentLoaded,
    selectedMillsIds,
    onMillSelectedChange,
    disableMillExecutiveFilter,
}: IRCAExecutiveMillFilter) => {
    const { mills } = RCALibraryEntity.GetMills();
    const { user_id } = useContext(UserContext);
    const { currentUser } = UsersEntity.GetCurrentUser(user_id || 0);
    const { t } = useTranslation();
    const [millsFilterSelected, setMillsFilterSelected] = useState(defaultMillsFilterOptionsSelected);
    const [millsOptions, setMillsOptions] = useState([] as IOption[]);

    const classes = useStyles();
    const mainClasses = useRCAMainStyles();

    const millFilterDropdownOptions = useMemo(
        () =>
            [
                {
                    id: currentUserCorporationSelection,
                    name: t("rca.executive_mill_filter.corporation"),
                },
                {
                    id: currentUserDivisionSelection,
                    name: t("rca.executive_mill_filter.my_division"),
                },
                {
                    id: currentUserRegionSelection,
                    name: t("rca.executive_mill_filter.my_region"),
                },
                {
                    id: currentUserMillSelection,
                    name: t("rca.executive_mill_filter.my_mill"),
                },
            ] as IOption[],
        [t],
    );

    const currentUserCorporation = useMemo(() => currentUser?.mill?.corporate_id, [currentUser]);
    const currentUserDivision = useMemo(() => currentUser?.mill?.division_id, [currentUser]);
    const currentUserRegion = useMemo(() => currentUser?.mill?.region_id, [currentUser]);
    const currentUserMill = useMemo(() => currentUser?.mill?.mill_id, [currentUser]);

    const corporationMillOptions = useMemo(() => {
        const filteredMills = mills.filter((mill) =>
            currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE
                ? true
                : mill.corporate_id === currentUserCorporation,
        );
        return filteredMills.map((mill) => ({
            id: mill.mill_id,
            name: mill.name,
        })) as IOption[];
    }, [currentUserCorporation, mills, currentUser]);

    const divisionMillOptions = useMemo(() => {
        const filteredMills = mills.filter((mill) =>
            currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE
                ? true
                : mill.division_id === currentUserDivision,
        );
        return filteredMills.map((mill) => ({
            id: mill.mill_id,
            name: mill.name,
        })) as IOption[];
    }, [currentUserDivision, mills, currentUser]);

    const regionMillOptions = useMemo(() => {
        const filteredMills = mills.filter((mill) =>
            currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE
                ? true
                : mill.region_id === currentUserRegion,
        );
        return filteredMills.map((mill) => ({
            id: mill.mill_id,
            name: mill.name,
        })) as IOption[];
    }, [currentUserRegion, mills, currentUser]);

    const userMillOption = useMemo(() => {
        const filteredMills = mills.filter((mill) =>
            currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE
                ? true
                : mill.mill_id === currentUserMill,
        );
        return filteredMills.map((mill) => ({
            id: mill.mill_id,
            name: mill.name,
        })) as IOption[];
    }, [currentUserMill, mills, currentUser]);

    const onMillDropdownChange = (value: string | number) => {
        const newValue = value as string;
        setMillsFilterSelected(newValue);
        const newMillOptions = [
            ...(newValue.includes(currentUserCorporationSelection) ? corporationMillOptions : []),
            ...(newValue.includes(currentUserDivisionSelection) ? divisionMillOptions : []),
            ...(newValue.includes(currentUserRegionSelection) ? regionMillOptions : []),
            ...(newValue.includes(currentUserMillSelection) ? userMillOption : []),
        ];

        const seen = {} as Record<string, boolean>;
        const newMillOptionsUnique = newMillOptions.filter(function (millOption) {
            return seen.hasOwnProperty(millOption.id) ? false : (seen[millOption.id as keyof typeof seen] = true);
        });
        setMillsOptions([...newMillOptionsUnique]);
        onSetExecutiveMillFilters({
            millsIds: [...newMillOptionsUnique.map((mill) => mill.id as number)],
        });
    };

    useEffect(() => {
        if (mills.length && userMillOption.length && currentUser?.mill?.mill_id && millsOptions.length === 0) {
            setMillsFilterSelected(defaultMillsFilterOptionsSelected);
            setMillsOptions([...userMillOption]);
            onComponentLoaded([currentUser?.mill?.mill_id]);
        } else if (
            mills.length &&
            corporationMillOptions.length &&
            millsOptions.length === 0 &&
            (currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
                currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE)
        ) {
            setMillsFilterSelected(currentUserCorporationSelection);
            setMillsOptions([...corporationMillOptions]);
            onComponentLoaded(mills.map((mill) => mill.mill_id));
        }
    }, [
        corporationMillOptions,
        corporationMillOptions.length,
        currentUser,
        mills,
        millsOptions.length,
        onComponentLoaded,
        t,
        userMillOption,
    ]);

    if (disableMillExecutiveFilter) return null;

    return (
        <>
            <Box display={"none"}>
                <InputLabel>
                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                        {t("rca.executive_mill_filter_labels.location")}
                    </Typography>
                </InputLabel>
                <NewMainSelect
                    className={classes.select}
                    fullWidth={false}
                    value={millsFilterSelected}
                    options={millFilterDropdownOptions}
                    onChange={onMillDropdownChange}
                />
            </Box>
            <Box>
                <InputLabel>
                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                        {t("rca.executive_mill_filter_labels.mill")}
                    </Typography>
                </InputLabel>
                <NewMainSelect
                    className={classes.select}
                    multiple
                    placeholder="Select mills"
                    fullWidth={false}
                    value={selectedMillsIds}
                    options={millsOptions}
                    onChange={(value: (string | number)[]) => {
                        onMillSelectedChange(value);
                    }}
                />
            </Box>
        </>
    );
};

export default RCAExecutiveMillFilter;
